import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const SENTINEL_COOKIE = 'af-register';

// Le store est persisté en localStorage (partagé entre onglets, sinon le lien
// d'activation reçu par email repart sans contexte d'inscription), et localStorage
// n'a pas d'expiration. La sentinelle en tient lieu : à son expiration, le garde de
// app.ts vide le store au démarrage. Une heure, comme la session d'af/auth — le
// délai court à partir de la saisie de l'email (voir le watch plus bas).
const SENTINEL_TTL_SECONDS = 60 * 60;

export function hasSentinel(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith(SENTINEL_COOKIE + '='));
}

function setSentinel() {
  document.cookie = `${SENTINEL_COOKIE}=1; path=/; max-age=${SENTINEL_TTL_SECONDS}; SameSite=Lax`;
}

function clearSentinel() {
  document.cookie = `${SENTINEL_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

import type { CompanyBranding } from '@/utils/branding';

export type RegisterScope = 'personal' | 'business';

export type JobTitle = {
  id?: number;
  label: string;
};

export type CompanyData = {
  label: string; // company name
  role: string; // role/job title at company
  project: string[]; // selected project goals
  number_of_staff: string;
};

export type RegisterState = {
  scope: RegisterScope | null;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: JobTitle | null;
  phone: string;
  dialCode: string;
  password: string;
  tou: boolean;
  commercial: boolean;
  // Business-only
  country: number | null;
  company: CompanyData | null;
  // Set after successful register call
  uuid: string;
  // SSO
  sso: boolean;
  providerInfos: {
    provider: string;
    token: string;
    data?: { id: string; email: string; first_name: string; last_name: string } | null;
  } | null;
  // Company branding (set during screening / token lookup)
  branding: CompanyBranding | null;
  // Invite params from URL (token, invited_by_recruiter, sharepool, utm_*, etc.)
  inviteParams: Record<string, string> | null;
};

export const useRegisterStore = defineStore(
  'register',
  () => {
    const scope = ref<RegisterScope | null>(null);
    const email = ref('');
    const firstName = ref('');
    const lastName = ref('');
    const jobTitle = ref<JobTitle | null>(null);
    const phone = ref('');
    const dialCode = ref('');
    const password = ref('');
    const tou = ref(false);
    const commercial = ref(false);
    const country = ref<number | null>(null);
    const company = ref<CompanyData | null>(null);
    const uuid = ref('');
    const sso = ref(false);
    const providerInfos = ref<{
      provider: string;
      token: string;
      data?: { id: string; email: string; first_name: string; last_name: string } | null;
    } | null>(null);
    const branding = ref<CompanyBranding | null>(null);
    const inviteParams = ref<Record<string, string> | null>(null);

    function setScope(value: RegisterScope) {
      scope.value = value;
    }

    function setEmail(value: string) {
      email.value = value;
    }

    function setIntroduce(data: { firstName: string; lastName: string; jobTitle: JobTitle | null }) {
      firstName.value = data.firstName;
      lastName.value = data.lastName;
      jobTitle.value = data.jobTitle;
    }

    function setConfirmation(data: {
      phone: string;
      dialCode: string;
      password: string;
      tou: boolean;
      commercial: boolean;
    }) {
      phone.value = data.phone;
      dialCode.value = data.dialCode;
      password.value = data.password;
      tou.value = data.tou;
      commercial.value = data.commercial;
    }

    function setWorkspace(data: { companyLabel: string; role: string; country: number }) {
      country.value = data.country;
      company.value = {
        label: data.companyLabel,
        role: data.role,
        project: company.value?.project ?? [],
        number_of_staff: company.value?.number_of_staff ?? ''
      };
    }

    function setProjects(projects: string[]) {
      company.value = {
        label: company.value?.label ?? '',
        role: company.value?.role ?? '',
        project: projects,
        number_of_staff: company.value?.number_of_staff ?? ''
      };
    }

    function setCompanySize(size: string) {
      company.value = {
        label: company.value?.label ?? '',
        role: company.value?.role ?? '',
        project: company.value?.project ?? [],
        number_of_staff: size
      };
    }

    function setUuid(value: string) {
      uuid.value = value;
    }

    function setBranding(value: CompanyBranding | null) {
      branding.value = value;
    }

    function setInviteParams(value: Record<string, string> | null) {
      inviteParams.value = value;
    }

    function setSso(value: boolean) {
      sso.value = value;
    }

    function setProviderInfos(
      value: {
        provider: string;
        token: string;
        data?: { id: string; email: string; first_name: string; last_name: string } | null;
      } | null
    ) {
      providerInfos.value = value;
    }

    function reset() {
      scope.value = null;
      email.value = '';
      firstName.value = '';
      lastName.value = '';
      jobTitle.value = null;
      phone.value = '';
      dialCode.value = '';
      password.value = '';
      tou.value = false;
      commercial.value = false;
      country.value = null;
      company.value = null;
      uuid.value = '';
      sso.value = false;
      providerInfos.value = null;
      branding.value = null;
      inviteParams.value = null;
      clearSentinel();
    }

    // Keep sentinel cookie in sync with store data so clearing cookies
    // also invalidates the persisted state — et lui donne son expiration.
    watch(email, val => {
      if (val) setSentinel();
    });

    return {
      scope,
      email,
      firstName,
      lastName,
      jobTitle,
      phone,
      dialCode,
      password,
      tou,
      commercial,
      country,
      company,
      uuid,
      sso,
      providerInfos,
      branding,
      inviteParams,
      setScope,
      setEmail,
      setIntroduce,
      setConfirmation,
      setWorkspace,
      setProjects,
      setCompanySize,
      setUuid,
      setBranding,
      setInviteParams,
      setSso,
      setProviderInfos,
      reset
    };
  },
  {
    persist: {
      // localStorage et non sessionStorage : ce dernier est cloisonné par onglet,
      // et le lien d'activation reçu par email s'ouvre dans un nouvel onglet — le
      // parcours y perdait scope, branding et invitations. Durée de vie bornée par
      // le cookie sentinelle ci-dessus.
      storage: localStorage,
      // Never persist sensitive credentials
      // password is intentionally excluded — never persisted to disk
      pick: [
        'scope',
        'email',
        'firstName',
        'lastName',
        'jobTitle',
        'phone',
        'dialCode',
        'tou',
        'commercial',
        'country',
        'company',
        'uuid',
        'sso',
        'providerInfos',
        'branding',
        'inviteParams'
      ]
    }
  }
);
